import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Skeleton,
  SwitchField,
  Tabs,
  TextAreaField,
  TextField,
  type TabItem,
} from '@tb/ui';
import {
  siteSettingsSchema,
  type AssetUploadResponse,
  type SaveDraftRequest,
  type SiteSettings,
  type SiteSource,
} from '@tb/shared';
import { api } from '../api/endpoints';
import { useCustomCode } from '../billing/useCustomCode';
import { Screen } from '../components/Layout';
import { useT } from '../i18n/LocaleProvider';
import { useRuntimeConfig } from '../RuntimeConfigProvider';
import { haptic, webApp } from '../telegram/telegram';
import { AssetManager } from './editor/AssetManager';
import { CodeEditor } from './editor/CodeEditor';
import { PreviewPane } from './editor/PreviewPane';
import { TemplateForm } from './editor/TemplateForm';

type Tab = 'content' | 'files' | 'settings' | 'preview';

const AUTOSAVE_DELAY_MS = 1200;

export function EditorScreen() {
  const { siteId = '' } = useParams();
  const navigate = useNavigate();
  const config = useRuntimeConfig();
  const queryClient = useQueryClient();
  const customCode = useCustomCode();
  const t = useT();

  const [tab, setTab] = useState<Tab>('content');
  const [source, setSource] = useState<SiteSource | null>(null);
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [assetIds, setAssetIds] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const dirty = useRef(false);

  const siteQuery = useQuery({ queryKey: ['site', siteId], queryFn: () => api.sites.get(siteId) });

  const draftVersionId = siteQuery.data?.draftVersion?.id ?? siteQuery.data?.liveVersion?.id;

  const sourceQuery = useQuery({
    queryKey: ['version-source', siteId, draftVersionId],
    queryFn: () => api.sites.versionSource(siteId, draftVersionId!),
    enabled: Boolean(draftVersionId),
  });

  const templatesQuery = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.templates.list(),
    staleTime: 5 * 60_000,
  });

  const assetsQuery = useQuery({
    queryKey: ['assets', siteId],
    queryFn: () => api.assets.list(siteId),
  });

  useEffect(() => {
    if (!sourceQuery.data || source !== null) return;
    setSource(sourceQuery.data.source);
    setSettings(siteSettingsSchema.parse(sourceQuery.data.settings));
    setAssetIds(sourceQuery.data.assetIds);
  }, [sourceQuery.data, source]);

  const draft: SaveDraftRequest | null = useMemo(
    () => (source && settings ? { source, settings, assetIds } : null),
    [source, settings, assetIds],
  );

  const saveMutation = useMutation({
    mutationFn: (body: SaveDraftRequest) => api.sites.saveDraft(siteId, body),
    onSuccess: () => {
      dirty.current = false;
      void queryClient.invalidateQueries({ queryKey: ['site', siteId] });
    },
  });

  useEffect(() => {
    if (!draft || !dirty.current) return;
    const timer = window.setTimeout(() => saveMutation.mutate(draft), AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = useCallback((mutate: () => void) => {
    dirty.current = true;
    mutate();
  }, []);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.assets.upload(siteId, file),
    onSuccess: (asset: AssetUploadResponse) => {
      setUploadError(null);
      update(() => setAssetIds((ids) => (ids.includes(asset.id) ? ids : [...ids, asset.id])));
      void queryClient.invalidateQueries({ queryKey: ['assets', siteId] });
      haptic('light');
    },
    onError: (error: Error) => setUploadError(error.message),
  });

  const uploadAsset = useCallback(
    async (file: File): Promise<AssetUploadResponse | null> => {
      try {
        return await uploadMutation.mutateAsync(file);
      } catch {
        return null;
      }
    },
    [uploadMutation],
  );

  const removeAssetMutation = useMutation({
    mutationFn: (assetId: string) => api.assets.remove(siteId, assetId),
    onSuccess: (_result, assetId) => {
      update(() => setAssetIds((ids) => ids.filter((id) => id !== assetId)));
      void queryClient.invalidateQueries({ queryKey: ['assets', siteId] });
    },
  });

  const leave = () => {
    if (draft && dirty.current) saveMutation.mutate(draft);
    void navigate(`/sites/${siteId}`);
  };

  if (
    siteQuery.isPending ||
    sourceQuery.isPending ||
    templatesQuery.isPending ||
    !source ||
    !settings
  ) {
    return (
      <Screen back={`/sites/${siteId}`}>
        <div className="tb-screen">
          <Skeleton height={44} />
          <Skeleton height={220} />
        </div>
      </Screen>
    );
  }

  if (siteQuery.isError || sourceQuery.isError || templatesQuery.isError) {
    return (
      <Screen back={`/sites/${siteId}`}>
        <div className="tb-screen">
          <Alert tone="danger" title={t('editor.openFailed')}>
            {((siteQuery.error ?? sourceQuery.error ?? templatesQuery.error) as Error).message}
          </Alert>
        </div>
      </Screen>
    );
  }

  const site = siteQuery.data;
  const assets = assetsQuery.data ?? [];
  const manifest = templatesQuery.data?.find(
    (entry) => source.kind === 'TEMPLATE' && entry.code === source.templateCode,
  );

  const tabs: TabItem<Tab>[] = [
    {
      id: 'content',
      label: source.kind === 'TEMPLATE' ? t('editor.tab.content') : t('editor.tab.code'),
      icon: source.kind === 'TEMPLATE' ? 'text' : 'code',
    },
    { id: 'files', label: t('editor.tab.files'), icon: 'image' },
    { id: 'settings', label: t('editor.tab.page'), icon: 'settings' },
    { id: 'preview', label: t('editor.tab.preview'), icon: 'preview' },
  ];

  return (
    <Screen title={site.domain} back={leave}>
      <div className="editor">
        <div style={{ padding: 'var(--tb-space-3) var(--tb-space-4) 0' }}>
          <Tabs items={tabs} value={tab} onChange={setTab} ariaLabel="Editor sections" />
        </div>

        <div className="editor__body">
          {tab === 'content' && source.kind === 'TEMPLATE' ? (
            manifest ? (
              <TemplateForm
                manifest={manifest}
                value={source.data}
                assets={assets}
                onRequestUpload={() => setTab('files')}
                onUpload={uploadAsset}
                onChange={(data) => update(() => setSource({ ...source, data }))}
              />
            ) : (
              <Alert tone="warning" title={t('editor.templateGone.title')}>
                {t('editor.templateGone.body')}
              </Alert>
            )
          ) : null}

          {tab === 'content' && source.kind === 'CUSTOM' ? (
            <CodeEditor
              source={source}
              dark={webApp()?.colorScheme === 'dark'}
              onChange={(next) => update(() => setSource(next))}
              notice={
                customCode.unlocked ? undefined : (
                  <Alert tone="warning" icon="lock" title={t('site.needsCustomCode.title')}>
                    <div className="tb-stack" style={{ gap: 10 }}>
                      <span>{t('site.needsCustomCode.body')}</span>
                      <Button icon="sparkles" size="sm" onClick={() => void navigate('/plans')}>
                        {t('billing.unlockCode')}
                      </Button>
                    </div>
                  </Alert>
                )
              }
            />
          ) : null}

          {tab === 'files' ? (
            <AssetManager
              assets={assets}
              uploading={uploadMutation.isPending}
              error={uploadError}
              maxAssetBytes={config.limits.maxAssetBytes}
              copyable={source.kind === 'CUSTOM'}
              onUpload={(file) => uploadMutation.mutate(file)}
              onRemove={(assetId) => removeAssetMutation.mutate(assetId)}
            />
          ) : null}

          {tab === 'settings' ? (
            <SettingsForm
              settings={settings}
              onChange={(next) => update(() => setSettings(next))}
            />
          ) : null}

          {tab === 'preview' && draft ? <PreviewPane siteId={siteId} draft={draft} /> : null}
        </div>

        <div className="tb-actionbar">
          <Button
            variant="secondary"
            icon="save"
            loading={saveMutation.isPending}
            onClick={() => draft && saveMutation.mutate(draft)}
          >
            {t('editor.save')}
          </Button>
          <Button
            className="tb-grow"
            icon="publish"
            onClick={() => {
              if (draft) saveMutation.mutate(draft);
              void navigate(`/sites/${siteId}/publish`);
            }}
          >
            {t('editor.publish')}
          </Button>
        </div>

        {saveMutation.isError ? (
          <div style={{ padding: '0 var(--tb-space-4) var(--tb-space-3)' }}>
            <Alert tone="danger" title={t('editor.saveFailed')}>
              {(saveMutation.error as Error).message}
            </Alert>
          </div>
        ) : null}
      </div>
    </Screen>
  );
}

function SettingsForm({
  settings,
  onChange,
}: {
  settings: SiteSettings;
  onChange: (settings: SiteSettings) => void;
}) {
  const t = useT();
  return (
    <div className="tb-stack" style={{ gap: 18 }}>
      <TextField
        label={t('editor.settings.title')}
        hint={t('editor.settings.titleHint')}
        maxLength={120}
        value={settings.title}
        onChange={(event) => onChange({ ...settings, title: event.target.value })}
      />
      <TextAreaField
        label={t('editor.settings.description')}
        hint={t('editor.settings.descriptionHint')}
        maxLength={300}
        value={settings.description}
        onChange={(event) => onChange({ ...settings, description: event.target.value })}
      />
      <TextField
        label={t('editor.settings.language')}
        hint={t('editor.settings.languageHint')}
        maxLength={8}
        value={settings.language}
        onChange={(event) => onChange({ ...settings, language: event.target.value })}
      />
      <SwitchField
        label={t('editor.settings.form')}
        hint={t('editor.settings.formHint')}
        checked={settings.contactFormEnabled}
        onChange={(contactFormEnabled) => onChange({ ...settings, contactFormEnabled })}
      />
    </div>
  );
}
